import {
  BrandKitAssets,
  getSignedUrlFromS3Path,
  useGetAssets,
  useUploadAssets,
} from '@/adapters/assets';
import BrandKitVideoItem from '@/components/BrandKitVideoItem';
import React, { FC, useContext, useEffect, useMemo, useState } from 'react';
// import Assets from '../../entities/brand-kit-list-item';
import { FileUpload } from '@/components/FileUpload';
import { UserContext } from '@/contexts/UserContext';
import { useParams } from 'react-router-dom';
import Loading from '../../components/Loading';
import { BrandKitAsset } from '../../entities/brand-kit-list-item';
import styles from './BrandKit.module.scss';
// import colorIcon from '@/assets/images/paint.svg';
import { UserSubscriptionState } from '@/entities/subscription';
// import FontPicker from 'font-picker-react';
// import { SelectChangeEvent } from '@mui/material';
// import FormControl from '@mui/material/FormControl';
// import Select, { SelectChangeEvent } from '@mui/material/Select';
// import MenuItem from '@mui/material/MenuItem';
// import { useUploadColor } from '../../adapters/assets';
import { useGetEvent, useUpdateEvent } from '@/adapters/events-v2';
import ApiV2 from '@/adapters/api-v2';

/* istanbul ignore file */
{
  /* eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call */
}

/**
 * Extracts S3 path from brand kit asset, handling multiple formats:
 * - Plain format (new): { path: "s3://..." }
 * - DynamoDB marshaled: { M: { path: { S: "s3://..." } } }
 * - Corrupted triple-nested: { M: { M: { M: { path: { M: { S: { S: "s3://..." } } } } } } }
 *
 * @reason Data may be in various formats due to marshaling inconsistencies
 */
function extractBrandKitPath(asset: unknown): string | null {
  if (!asset || typeof asset !== 'object') return null;

  const obj = asset as Record<string, unknown>;

  // Plain format: { path: "s3://..." }
  if (typeof obj.path === 'string') {
    return obj.path;
  }

  // DynamoDB marshaled format: { M: { path: { S: "s3://..." } } }
  if (obj.M && typeof obj.M === 'object') {
    const m = obj.M as Record<string, unknown>;
    // Check for triple-nested corruption: { M: { M: { M: { path: ... } } } }
    if (m.M && typeof m.M === 'object') {
      return extractBrandKitPath(m.M);
    }
    if (m.path && typeof m.path === 'object') {
      const pathObj = m.path as Record<string, unknown>;
      if (typeof pathObj.S === 'string') {
        return pathObj.S;
      }
      // Handle { path: { M: { S: { S: "..." } } } } corruption
      if (pathObj.M && typeof pathObj.M === 'object') {
        const pathM = pathObj.M as Record<string, unknown>;
        if (pathM.S && typeof pathM.S === 'object') {
          const pathS = pathM.S as Record<string, unknown>;
          if (typeof pathS.S === 'string') {
            return pathS.S;
          }
        }
      }
    }
  }

  return null;
}

const BrandKit: FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const {
    data: eventData,
    isLoading: isEventLoading,
    refetch: refetchEvent,
  } = useGetEvent(eventId);
  // Only fetch global assets if there's no eventId
  const { data: brandKit, isLoading } = useGetAssets(!eventId);
  const { mutateAsync: uploadBrandKit } = useUploadAssets();
  const { mutateAsync: updateEvent } = useUpdateEvent(eventId || '');
  // const { mutateAsync: uploadBrandFont } = useUploadFonts();
  // const { mutateAsync: uploadBrandColor } = useUploadColor();
  const { userState } = useContext(UserContext);
  const [state, setState] = useState<UserSubscriptionState[]>([]);
  console.log('🚀 ~ BrandKit ~ state:', state);
  const [signedUrls, setSignedUrls] = useState<{
    intro: string | null;
    logo: string | null;
    outro: string | null;
  }>({
    intro: null,
    logo: null,
    outro: null,
  });
  const [isConvertingUrls, setIsConvertingUrls] = useState(false);
  // const [selectedColor, setSelectedColor] = useState<string | null>(null);
  // const [activeFontFamily, setActiveFontFamily] = useState<string>(
  //   // initialActiveFontFamily ??
  //   'Roboto',
  // );
  // const [logoPosition, setLogoPosition] = useState<string>('Top Right');

  //Function has a switch case which returns a ReactNode with the correct copy for the Brand Kit
  const getDescription = (type: string): React.ReactNode => {
    switch (type) {
      case 'intro':
        return (
          <>
            This is the title at the beginning of your video. Choose a <b>png</b> image or a short{' '}
            <b>mp4</b> up to <b>1920x1080</b> pixels with a maximum size of 100Mb.
          </>
        );
      case 'outro':
        return (
          <>
            This is the title at the end of your video. Choose a <b>png</b> image or a short{' '}
            <b>mp4</b> up to <b>1920x1080</b> pixels with a maximum size of 100Mb.
          </>
        );
      case 'logo':
        return (
          <>
            Your video logo is the core part of your video. It will be displayed throughout your
            video and should be defined as a trasparent PNG and maximum size of 5Mb.
          </>
        );
      case 'font':
        return <>Your primary font will apply to any text overlays on your videos.</>;
      case 'color':
        return <>Your text color will apply to any text overlays on your videos.</>;
      default:
        return null;
    }
  };

  useEffect(() => {
    if (userState) {
      setState(
        userState.SubscriptionState.filter(
          (sub) =>
            sub.plan === process.env.REACT_APP_STRIPE_PLAN_PRO_ANNUAL ||
            sub.plan === process.env.REACT_APP_STRIPE_PLAN_PRO_MONTH,
        ),
      );
    }
  }, [userState]);

  // const handleLogoPosition = (event: SelectChangeEvent): void => {
  //   setLogoPosition(event.target.value);
  // };

  // const uploadFont = async (font: string): Promise<void> => {
  //   await uploadBrandFont({ type: 'font', font: font });
  // };

  // const uploadColor = async (color: string): Promise<void> => {
  //   await uploadBrandColor({ type: 'color', color: color });
  // };

  // const handleColorChange = async (event: SelectChangeEvent): Promise<void> => {
  //   setSelectedColor(event.target.value);
  //   await uploadColor(event.target.value);
  // };
  // const handleTextFieldChange = async (
  //   event: React.ChangeEvent<HTMLInputElement>,
  // ): Promise<void> => {
  //   setSelectedColor(event.target.value);
  //   await uploadColor(event.target.value);
  // };

  // Convert S3 paths to signed URLs when eventData changes
  useEffect(() => {
    const convertS3PathsToSignedUrls = async (): Promise<void> => {
      if (eventId && eventData) {
        setIsConvertingUrls(true);
        const bk = eventData.brandKit as Record<string, unknown> | null;
        if (bk) {
          const introS3Path = extractBrandKitPath(bk.intro);
          const logoS3Path = extractBrandKitPath(bk.logo);
          const outroS3Path = extractBrandKitPath(bk.outro);

          const conversions = await Promise.allSettled([
            introS3Path && introS3Path.startsWith('s3://')
              ? getSignedUrlFromS3Path(introS3Path)
              : Promise.resolve(introS3Path || null),
            logoS3Path && logoS3Path.startsWith('s3://')
              ? getSignedUrlFromS3Path(logoS3Path)
              : Promise.resolve(logoS3Path || null),
            outroS3Path && outroS3Path.startsWith('s3://')
              ? getSignedUrlFromS3Path(outroS3Path)
              : Promise.resolve(outroS3Path || null),
          ]);

          setSignedUrls({
            intro: conversions[0].status === 'fulfilled' ? conversions[0].value : null,
            logo: conversions[1].status === 'fulfilled' ? conversions[1].value : null,
            outro: conversions[2].status === 'fulfilled' ? conversions[2].value : null,
          });
        } else {
          setSignedUrls({ intro: null, logo: null, outro: null });
        }
        setIsConvertingUrls(false);
      } else {
        setSignedUrls({ intro: null, logo: null, outro: null });
        setIsConvertingUrls(false);
      }
    };

    void convertS3PathsToSignedUrls();
  }, [eventId, eventData]);

  const displayBrandKit = useMemo<BrandKitAsset | undefined>(() => {
    // If eventId is present, use event's brandKit with converted signed URLs
    if (eventId && eventData) {
      return {
        intro: { path: signedUrls.intro },
        logo: { path: signedUrls.logo },
        outro: { path: signedUrls.outro },
      };
    }
    if (!brandKit) {
      return undefined;
    }

    return {
      intro: brandKit.intro,
      logo: brandKit.logo,
      outro: brandKit.outro,
    };
  }, [eventId, eventData, brandKit, signedUrls]);

  const mapToBrandKitAsset = (asset: 'intro' | 'logo' | 'outro'): BrandKitAssets => {
    switch (asset) {
      case 'intro':
        return BrandKitAssets.intro;
      case 'logo':
        return BrandKitAssets.logo;
      case 'outro':
      default:
        return BrandKitAssets.outro;
    }
  };

  const uploadEventBrandKitAsset = async (
    type: BrandKitAssets,
    file: File,
  ): Promise<{ s3Path: string }> => {
    if (!eventId) {
      throw new Error('Event ID is required for event brand kit upload');
    }

    const response = await ApiV2.post<{
      success: boolean;
      data?: { url: string; s3Path: string };
      error?: string;
    }>(`/api/events/${eventId}/brandkit/upload-url/${type}`, {
      fileName: file.name,
    });

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to get upload URL for event brand kit');
    }

    const { url, s3Path } = response.data;

    const uploadResponse = await fetch(url, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    });

    if (!uploadResponse.ok) {
      throw new Error(`Event brand kit upload failed: ${uploadResponse.statusText}`);
    }

    return { s3Path };
  };

  // Handle upload for both global and event-specific brand kits
  const handleUpload = async (type: 'intro' | 'logo' | 'outro', file: File): Promise<void> => {
    try {
      if (eventId) {
        // For event-specific brand kit: upload to S3 with event-specific path, then update event
        const { s3Path } = await uploadEventBrandKitAsset(mapToBrandKitAsset(type), file);
        // Build clean brand kit with plain format { path: "s3://..." }
        // Backend uses DynamoDBDocumentClient which handles marshaling automatically
        const currentBrandKit = eventData?.brandKit as Record<string, unknown> | null;
        const updatedBrandKit = {
          intro: currentBrandKit ? { path: extractBrandKitPath(currentBrandKit.intro) } : null,
          logo: currentBrandKit ? { path: extractBrandKitPath(currentBrandKit.logo) } : null,
          outro: currentBrandKit ? { path: extractBrandKitPath(currentBrandKit.outro) } : null,
          [type]: { path: s3Path },
        };
        // Update event with new brandKit in plain format
        await updateEvent({ brandKit: updatedBrandKit });
        // Refresh event data to show new asset
        await refetchEvent();

        // The useEffect will automatically convert the new S3 path to signed URL
      } else {
        // For global brand kit: just upload (existing behavior)
        await uploadBrandKit({ type: mapToBrandKitAsset(type), file });
      }
    } catch (error) {
      console.error('Failed to upload brand kit asset:', error);
      throw error;
    }
  };

  return (
    <div className={styles.brandKit}>
      <h1 className={styles.brandKitHeadTitle}>Brand Kit</h1>
      {isLoading || isEventLoading || isConvertingUrls ? (
        <Loading />
      ) : displayBrandKit ? (
        (['intro', 'logo', 'outro'] as const).map((label, index: number) => {
          const path = displayBrandKit[label].path;

          return (
            <div className={styles.brandKitItem} key={index}>
              <div className={styles.brandKitItemLeft}>
                <div className={styles.brandKitItemElem}>
                  <div className={styles.brandKitItemTitle}>
                    {label.charAt(0).toUpperCase() + label.slice(1)}
                  </div>
                  <div className={styles.brandKitItemText}>{getDescription(label)}</div>
                </div>
              </div>
              <div
                className={styles.brandKitItemRight}
                // Subscription blur and redirect logic commented out - brand kit should be accessible
                // style={{
                //   opacity:
                //     state.length === 0 && userState && userState.trialDays === 0 ? '0.5' : '10',
                // }}
                // onClick={(event) => {
                //   if (state.length === 0 && userState && userState.trialDays === 0) {
                //     event.preventDefault();
                //     // navigate('/subscription/plans');
                //   }
                // }}
              >
                <div className={[styles.brandKitItemElem, styles.brandKitItemElemRow].join(' ')}>
                  {path && (
                    <div className={styles.brandKitItemElemVideo}>
                      <BrandKitVideoItem path={path} controls />
                    </div>
                  )}
                  <div data-testid={'file'}>
                    <FileUpload
                      styleEl="panel"
                      fileTypes={['.png', '.mp4']}
                      onChange={(file: File) => handleUpload(label, file)}
                    />
                  </div>
                </div>
                {/* {label === 'logo' && (
                    <FormControl sx={{ width: '100%' }}>
                      <Select
                        value={logoPosition}
                        onChange={handleLogoPosition}
                        sx={{ height: '40px', borderRadius: '8px' }}
                      >
                        <MenuItem value={'Top Right'}>Top Right</MenuItem>
                        <MenuItem value={'Top Left'}>Top Left</MenuItem>
                        <MenuItem value={'Bottom Left'}>Bottom Left</MenuItem>
                        <MenuItem value={'Bottom Right'}>Bottom Right</MenuItem>
                      </Select>
                    </FormControl>
                  )} */}
              </div>
            </div>
          );
        })
      ) : (
        <></>
      )}
    </div>
  );
};

export default BrandKit;
